import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid, MenuItem, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { TypeOfMember } from "Util/enums/members";
import { getEquipmentName } from "Util/getEquipmentName";
import * as yup from "yup";

import { AutoSuggest, Input, Select } from "../../../../../../../components/Form";
import { Color } from "../../../../../../../components/Form/Button";
import Modal from "../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../context/StateContext";
import useYupValidation, { useHandleValidate } from "../../../../../../../hooks/useYupValidation";
import { Fleet } from "../../../../../../../models/fleet";
import { Member } from "../../../../../../../models/members/member";
import { Team } from "../../../../../../../models/team";
import FleetService from "../../../../../../../services/fleet.service";
import MembersService from "../../../../../../../services/members.service";
import { Status } from "../../../../../../../util/enums";
import { BranchKeys, FleetKeys, MemberKeys } from "../../../../../../../util/queries";
import useStyles, { BoxStyled, ChipStyled, SelectBranchOption } from "./styles";
import SkeletonComponent from "Components/Generic/Skeleton";
import BranchService from "Services/branch.service";
import { Branch } from "Models/branch";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";

interface Props {
  editTeam: (team: Team) => void;
  createTeam: (team: Team) => void;
  isLoading?: boolean;
}

export interface HandleTeamModalProps {
  handleOpenModal: (team?: Team) => void;
  handleCloseModal: (team?: Team) => void;
}

const TeamModal: ForwardRefRenderFunction<HandleTeamModalProps, Props> = (props, ref) => {
  const { editTeam, createTeam, isLoading = false } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const [team, setTeam] = useState<Team | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const handleOpenModal = (team?: Team) => {
    setOpen(true);
    if (team) setTeam(team);
  };

  const handleCloseModal = () => {
    setBranches([]);
    setOpen(false);
    setTeam(null);
    setErrors([]);
  };

  useImperativeHandle(ref, () => ({
    handleOpenModal,
    handleCloseModal,
  }));

  const { data: branchList, isFetching: fetchingBranchList } = useQuery({
    queryKey: BranchKeys.getAll([]),
    queryFn: async () =>
      await BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }).then(({ data }) => data),
    initialData() {
      return [];
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.companyId,
  });

  const { data: memberList } = useQuery({
    queryKey: MemberKeys.getAll(["people-team-modal-members"]),
    queryFn: async () => {
      const response = await MembersService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["fullName", "asc"]]),
        include: JSON.stringify([
          {
            association: "userCompany",
            required: false,
            where: {
              companyId: currentUser?.companyId,
            },
          },
        ]),
      });

      if (response?.data && Array.isArray(response?.data)) {
        return response.data;
      }

      return [] as Member[];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const memberListAvailable = useMemo(() => {
    if (!team || !team.memberLeaderId) return memberList || [];

    return (memberList || []).filter((member) => member.id !== team.memberLeaderId);
  }, [team, memberList]);

  const { data: fleetList } = useQuery({
    queryKey: FleetKeys.getAll([]),
    queryFn: async () => {
      const response = await FleetService.getAll({
        companyId: currentUser?.companyId,
        attributes: JSON.stringify([
          "id",
          "externalUuid",
          "description",
          "equipmentType",
          "fleetNumber",
        ]),
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const onSelectedBranch = (branchesId: number[]) => {
    if (branchList?.length && branchesId.includes(0)) {
      setBranches([...branchList]);
      setTeam(
        (state) =>
          ({
            ...state,
            branches: branchList,
          } as Team)
      );
    } else if (branchesId.includes(-1)) {
      setBranches([]);
      setTeam(
        (state) =>
          ({
            ...state,
            branches: [],
          } as Team)
      );
    } else {
      const branchArr: Branch[] = [];

      for (const branchId of branchesId) {
        const findBranch = branchList?.find((branch) => branch.id === Number(branchId));

        if (findBranch) branchArr.push(findBranch);
      }
      setBranches([...branchArr]);
      setTeam(
        (state) =>
          ({
            ...(state || {}),
            branches: branchArr,
          } as Team)
      );
    }
  };

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
    branches: yup.array().min(1, translate("itShouldHaveLeastOneBranch")),
  });

  const handleValidate = () => {
    const validated = useHandleValidate(
      {
        ...team,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const onSubmit = () => {
    if (team && handleValidate()) {
      if (team?.id)
        editTeam({
          ...team,
          memberLeaderId: team?.memberLeaderId || null,
        });
      else createTeam(team);
    }
  };

  const renderChipValue = (selected: number[]) => {
    const selectedBranches: Branch[] = [];

    for (const branchId of selected) {
      const findBranch = branches.find((branch) => branch.id === branchId);

      if (findBranch) selectedBranches.push(findBranch);
    }

    return (
      <BoxStyled>
        {selectedBranches?.map((value: Branch, index: number) => {
          if (index <= 1) return <ChipStyled key={value.id} label={value.name} />;

          return null;
        })}
      </BoxStyled>
    );
  };

  const leaderList = memberList
    ? memberList.filter(
        (member) => member.userCompany?.leader && member.typeOfMember === TypeOfMember.USER
      )
    : [];

  useEffect(() => {
    if (team?.id && team?.branches?.length) {
      setBranches(team?.branches);
    }
  }, [team]);

  return (
    <Modal
      className={classes.modalWrapper}
      open={open}
      handleClose={handleCloseModal}
      title={team?.id ? translate("editTeam") : translate("newTeam")}
      showActionButtons
      actionButtonProps={{
        textSave: team?.id ? translate("save") : translate("create"),
        customColorCancel: Color.TEXT_INFO,
        saveDisabled: isLoading,
        cancelDisabled: isLoading,
        loading: isLoading,
        onCancel: () => handleCloseModal(),
        onSave: onSubmit,
      }}
    >
      <Grid container flex-directio="column" spacing={3}>
        <Grid item xs={12}>
          <Input
            label={translate("name")}
            required
            value={team?.name}
            disabled={isLoading}
            onChange={(event) =>
              setTeam(() => ({ ...(team || ({} as Team)), name: event.target.value }))
            }
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
          />
        </Grid>
        <Grid item xs={12}>
          <AutoSuggest
            disabled={isLoading}
            label={translate("leaderUser")}
            inputValue={team?.memberLeader?.fullName || ""}
            getOptionLabel={(option: Member) => option?.fullName ?? ""}
            getOptionSelected={(option: Member, value: Member) => option?.id === value?.id}
            value={team?.memberLeaderId}
            onChange={(leader: Member) => {
              setTeam((prevState) => ({
                ...(prevState || ({} as Team)),
                memberLeader: leader !== null ? leader : undefined,
                memberLeaderId: leader !== null ? leader.id : undefined,
              }));
            }}
            options={leaderList?.length ? leaderList : []}
          />
        </Grid>
        <Grid item xs={12}>
          <AutoSuggest
            multiple
            disabled={isLoading}
            label={translate("staff")}
            getOptionLabel={(option: Member) => option?.fullName ?? ""}
            getOptionSelected={(option: Member, value: Member) => option?.id === value?.id}
            value={team?.members?.filter((member) => member.id !== team?.memberLeaderId) || []}
            onChange={(members: Member[]) =>
              setTeam(() => ({ ...(team || ({} as Team)), members }))
            }
            options={memberListAvailable?.length ? memberListAvailable : []}
          />
        </Grid>
        <Grid item xs={12}>
          <AutoSuggest
            multiple
            disabled={isLoading}
            label={translate("equipments")}
            getOptionLabel={(option: Fleet) => {
              const { fullText } = getEquipmentName(
                option,
                translate("general"),
                translate("fleet")
              );

              return fullText;
            }}
            getOptionSelected={(option: Fleet, value: Fleet) => option?.id === value?.id}
            value={team?.fleets || []}
            onChange={(fleets: Fleet[]) => setTeam(() => ({ ...(team || ({} as Team)), fleets }))}
            options={fleetList?.length ? fleetList : []}
          />
        </Grid>
        <Grid item xs={12}>
          <SkeletonComponent isLoading={fetchingBranchList}>
            <Select
              dataCy="selectBranch"
              label={translate("branch")}
              className={classes.select}
              name="branchList"
              value={branches?.length ? branches?.map((branch) => branch?.id || -2) : []}
              error={fieldHasError("branches")}
              errorMessage={getErrorByField("branches")}
              onChange={(event: SelectChangeEvent<any>) => onSelectedBranch(event.target.value)}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              multiple
              required
              renderValue={(selected) => renderChipValue(selected as number[])}
              placeholder={translate("select")}
            >
              {Number(branches?.length) < Number(branchList?.length) && (
                <MenuItem value={0}>
                  <SelectBranchOption>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </SelectBranchOption>
                </MenuItem>
              )}
              {branches?.length === branchList?.length && (
                <MenuItem value={-1}>
                  <SelectBranchOption isRemoving>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </SelectBranchOption>
                </MenuItem>
              )}
              {branchList?.map((branch) => (
                <MenuItem value={branch.id} key={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default forwardRef(TeamModal);
